import cn from "classnames"

import { Flex, Space, Text } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import { VmIcons } from "@/icons"
import { CopyLinkButton } from "@/molecules/CopyLinkButton"
import { TitleDrawer } from "@/molecules/TitleDrawer"
import { useVirtualMachineEntity } from "@/organisms/Drawers/applications/inventory/useInventory"
import { SkeletonTitle } from "@/organisms/Drawers/components/SkeletonTitle"
import { useLink } from "@/organisms/Drawers/hooks/useLink"

import styles from "./TitleVirtualMachineDrawer.module.scss"

import type { EntityRecord } from "@/organisms/Drawers/applications/common"

export const TitleVirtualMachineDrawer = (
  props: EntityRecord & { drawerId: string }
) => {
  const context = useVirtualMachineEntity(props.record?.id)
  const [{ response, isLoading }] = context || {}
  const { data } = response || {}
  const link = useLink(props.drawerId)
  const { t } = useTranslation()
  const { t: tDrawer } = useTranslation("translation", {
    keyPrefix: "inventory.vm.drawer"
  })

  if (isLoading || !data) return <SkeletonTitle />

  const { data: vm } = (data as any) || ({} as any)
  const {
    instanceId,
    autoScalingGroups,
    instanceType,
    imageId,
    groupInstancesCount
  } = vm || {}
  const autoScalingGroupsText = (autoScalingGroups || []).join(", ") || ""

  return (
    <TitleDrawer
      classes={{ title: styles.w100 }}
      icon={<VmIcons type={vm.subjectType} className={styles.icon} />}
      title={instanceId}
      subTitle={
        <Space
          className={cn(styles.w100, styles.space)}
          separator={
            <Text className={cn("sm-text-ks", styles.separator)}>|</Text>
          }
        >
          {!!instanceType && (
            <Text type="secondary" className="sm-text-ks">
              {tDrawer("instanceType")}: {instanceType}
            </Text>
          )}
          {!!imageId && (
            <Text ellipsis type="secondary" className="sm-text-ks">
              {tDrawer("machineImage")}: {imageId}
            </Text>
          )}
          {!!autoScalingGroupsText && (
            <Flex align="center" gap={4}>
              <Text ellipsis type="secondary" className="sm-text-ks">
                {autoScalingGroupsText}
              </Text>
              {!!groupInstancesCount && (
                <Text type="secondary" className="sm-text-ks">
                  {groupInstancesCount}{" "}
                  {t("general.instance", { count: groupInstancesCount })}
                </Text>
              )}
            </Flex>
          )}
        </Space>
      }
      aside={<CopyLinkButton link={link} />}
    />
  )
}
